'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Button, StarRating } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_GRAY,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { isRatingValue } from '@sgcmpteltd/uikit-react/components/star-rating';
import Link from 'next/link';
import { useCallback, useState } from 'react';

import { getFormattedDateString } from '@/utils/string/formattedDate';
import { joinMyCarForumSite } from '@/utils/url/join/public/util';

import {
  CardWithAvatar,
} from '../../../_components/page-content/card-with-avatar';
import { STAR_ICON_SIZE_IN_PIXEL } from '../../constants';
import { AvatarBottomItems } from './avatar-bottom-items';
import { rankMap, ratingCategories } from './config';
import {
  CONSUMER_DEFAULT_IMAGE,
  CONSUMER_REVIEW_ICON_16,
  CONSUMER_REVIEW_ICON_18,
  CONSUMER_REVIEW_ICON_20,
  MOBILE_READ_MORE,
  MOBILE_SHOW_LESS,
  SAD_ICON_SRC,
  SMILE_ICON_SRC,
  TEXT_REVIEW,
  TEXT_WHAT_I_DISLIKE,
  TEXT_WHAT_I_LIKE,
  TEXTBOX_ICON_SRC,
} from './constant';
import { ConsumerRatingScoreContent } from './consumer-rating-score';
import { ConsumerReviewContent } from './consumer-review-content';
import { RankPopupModel } from './rank-popup-model';
import { RankTooltipContent } from './rank-tool-tip';
import styles from './styles.module.scss';
import type { Component } from './types';

export const ConsumerReview: Component = ({
  consumerReview,
}) => {
  const [expandReviews, setExpandReviews] = useState<number | null>(null);
  const { isIpadMini } = useScreenDetect();
  const isIpadMiniScreen = isIpadMini();
  const showReadMoreButton = consumerReview.like
    || consumerReview.dislike
    || consumerReview.detailedRatings;
  const isExpandConsumerReview = expandReviews
    === consumerReview.id;
  const formattedOverallRate = (
    rate?: number | null,
  ) => (rate ? rate.toFixed(1) : '0.0');

  const findMemberRank = (points: number) => rankMap.slice().reverse().find(
    rank => points >= rank.points,
  );

  const getMemberRank = (points?: number | null): string => {
    if (points === undefined || points === null) {
      return 'Unknown';
    }
    const memberRank = findMemberRank(points);
    return memberRank ? memberRank.rank : 'Unknown';
  };

  const handleReadMoreButtonClick = useCallback(
    (id: number) => () => {
      setExpandReviews(prev => (prev === id ? null : id));
    },
    [],
  );
  return (
    <div
      key={consumerReview.id}
      className={styles.containerConsumerReview}
      id={`comments${consumerReview.id}`}>
      <CardWithAvatar
        avatar={consumerReview.member?.image ?? CONSUMER_DEFAULT_IMAGE}
        lowerChildNode={(
          isIpadMiniScreen && (
            <AvatarBottomItems consumerReview={consumerReview} />
          )
        )}
        showDivider={isIpadMiniScreen}
        upperChildNode={
          isIpadMiniScreen
            ? (
              <div className={styles.containerContent}>
                <div className={styles.containerName}>
                  <Link
                    className={styles.contentName}
                    href={joinMyCarForumSite(`/profile/${consumerReview.member?.id}-${consumerReview.member?.name}/`)}>
                    {consumerReview.member?.name}
                  </Link>
                </div>
                <div className={styles.containerGrade}>
                  <span className={styles.gradeRank}>
                    {getMemberRank(
                      consumerReview.member?.memberPoints,
                    )}
                  </span>
                  <RankTooltipContent />
                </div>
              </div>
            )
            : consumerReview.member && (
              <div className={styles.containerMobileContent}>
                <div className={styles.containerMobileName}>
                  <span className={styles.contentName}>
                    {consumerReview.member.name}
                  </span>
                  {' | '}
                  <div className={styles.containerGrade}>
                    <span className={styles.gradeRank}>
                      {getMemberRank(
                        consumerReview.member?.memberPoints,
                      )}
                    </span>
                    <RankPopupModel />
                  </div>
                </div>
              </div>
            // eslint-disable-next-line @stylistic/jsx-indent
            )
        } />
      <div className={styles.containerConsumerReviewBlock}>
        <div className={styles.containerConsumerReviewHead}>
          <div className={styles.consumerReviewTitle}>
            {consumerReview.title}
          </div>
          {
            isIpadMiniScreen && (
              <div className={styles.consumerReviewPostOn}>
                Posted on:
                {' '}
                {getFormattedDateString(
                  consumerReview.postedAt,
                  'shorten',
                  '-',
                )}
              </div>
            )
          }
        </div>
        {isIpadMiniScreen && (
          <div className={
            styles.containerConsumerReviewScore
          }>
            {
              consumerReview.rating && (
                <div className={
                  styles.conatinerConsumerOverallReviewScore
                }>
                  <div className={styles.consumerReviewScore}>
                    {formattedOverallRate(consumerReview.rating)}
                  </div>
                  <StarRating
                    iconSize={STAR_ICON_SIZE_IN_PIXEL}
                    value={isRatingValue(consumerReview.rating)
                      ? consumerReview.rating
                      : 0} />
                </div>
              )
            }
            {
              consumerReview.detailedRatings && (
                <div className={styles.containerRatingBlocks}>
                  {ratingCategories.map(({ key, label }) => (
                    <ConsumerRatingScoreContent
                      key={key}
                      label={label}
                      score={consumerReview.detailedRatings![
                        key as keyof typeof consumerReview.detailedRatings]} />
                  )) }
                </div>
              )
            }
          </div>
        )}
        {
          !isIpadMiniScreen && (
            <div>
              <StarRating
                iconSize={STAR_ICON_SIZE_IN_PIXEL}
                value={isRatingValue(consumerReview.rating)
                  ? consumerReview.rating
                  : 0} />
            </div>
          )
        }
        {
          consumerReview.review && (
            <ConsumerReviewContent
              content={consumerReview.review}
              iconSize={CONSUMER_REVIEW_ICON_18}
              iconSrc={TEXTBOX_ICON_SRC}
              title={TEXT_REVIEW} />
          )
        }
        {
          (consumerReview.like
          && (isExpandConsumerReview || isIpadMiniScreen))
          && (
            <ConsumerReviewContent
              content={consumerReview.like}
              iconSize={CONSUMER_REVIEW_ICON_16}
              iconSrc={SMILE_ICON_SRC}
              title={TEXT_WHAT_I_LIKE} />
          )
        }
        {
          (consumerReview.dislike
          && (isExpandConsumerReview || isIpadMiniScreen))
          && (
            <ConsumerReviewContent
              content={consumerReview.dislike}
              iconSize={CONSUMER_REVIEW_ICON_20}
              iconSrc={SAD_ICON_SRC}
              title={TEXT_WHAT_I_DISLIKE} />
          )
        }
        {
          !isIpadMiniScreen && isExpandConsumerReview
          && consumerReview.detailedRatings && (
            <>
              <div className={
                styles.containerConsumerReviewContent
              }>
                <div className={styles.sectionText}>
                  Overall Rating
                </div>
              </div>
              <div className={
                styles.containerConsumerReviewEachRating
              }>
                {ratingCategories.map(({ key, label }) => (
                  <ConsumerRatingScoreContent
                    key={key}
                    label={label}
                    score={consumerReview.detailedRatings![
                      key as keyof typeof consumerReview.detailedRatings]} />
                ))}
              </div>
            </>
          )
        }
        {
          !isIpadMiniScreen && (
            <div className={styles.consumerReviewPostOn}>
              Posted on:
              {' '}
              {getFormattedDateString(
                consumerReview.postedAt,
                'shorten',
                '-',
              )}
            </div>
          )
        }
      </div>
      {
        !isIpadMiniScreen && showReadMoreButton && (
          <Button
            className={styles.readMoreButton}
            color={BUTTON_COLOR_GRAY}
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={
              handleReadMoreButtonClick(consumerReview.id)
            }>
            <span className={styles.submitText}>
              {isExpandConsumerReview
                ? MOBILE_SHOW_LESS
                : MOBILE_READ_MORE}
            </span>
          </Button>
        )
      }
    </div>
  );
};
